'use client';

import React, { useState } from 'react';
import { Info, X, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';

interface ApiSetupBannerProps {
  lang?: 'hi' | 'en';
}

export default function ApiSetupBanner({ lang = 'hi' }: ApiSetupBannerProps) {
  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(false);

  if (dismissed) return null;

  return (
    <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl p-3 mb-4">
      <div className="flex items-start gap-3">
        <Info size={16} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="text-amber-800 dark:text-amber-300 text-xs font-hindi font-semibold">
              {lang === 'hi' ?'⚙️ लाइव न्यूज़ सेटअप — अभी सैंपल डेटा दिखाया जा रहा है' :'⚙️ Live News Setup — Currently showing sample data'}
            </p>
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={() => setExpanded(!expanded)}
                className="text-amber-700 dark:text-amber-400 hover:text-amber-900 transition-colors"
                aria-label="Toggle setup instructions"
              >
                {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
              <button
                onClick={() => setDismissed(true)}
                className="text-amber-700 dark:text-amber-400 hover:text-amber-900 transition-colors"
                aria-label="Dismiss banner"
              >
                <X size={14} />
              </button>
            </div>
          </div>

          {expanded && (
            <div className="mt-3 space-y-2 fade-in">
              <p className="text-amber-700 dark:text-amber-400 text-xs font-hindi leading-relaxed">
                {lang === 'hi' ?'लाइव झारखंड न्यूज़ फ़ीड के लिए NewsAPI.org से मुफ्त API key प्राप्त करें:' :'To get live Jharkhand news, get a free API key from NewsAPI.org:'}
              </p>
              <ol className="text-amber-700 dark:text-amber-400 text-xs space-y-1 list-decimal list-inside font-hindi">
                <li>
                  <a
                    href="https://newsapi.org/register"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-amber-900 inline-flex items-center gap-1"
                  >
                    newsapi.org/register
                    <ExternalLink size={10} />
                  </a>
                  {lang === 'hi' ? ' पर मुफ्त खाता बनाएं' : ' — Create free account'}
                </li>
                <li>
                  {lang === 'hi' ? '.env.local फ़ाइल में जोड़ें:' : 'Add to .env.local:'}
                  <code className="ml-1 px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900 rounded text-[10px] font-mono">
                    NEXT_PUBLIC_NEWS_API_KEY=your_key_here
                  </code>
                </li>
                <li>
                  {lang === 'hi' ?'ऐप रीस्टार्ट करें — लाइव खबरें अपने आप आने लगेंगी' :'Restart the app — live Jharkhand news will load automatically'}
                </li>
              </ol>
              <p className="text-amber-600 dark:text-amber-500 text-[10px] font-hindi mt-2">
                {lang === 'hi' ?'* NewsAPI फ्री टियर: 1000 requests/दिन — ऐप के लिए पर्याप्त' :'* NewsAPI free tier: 1000 requests/day — sufficient for this app'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}